import http from "./requestBase";
import { FILES_STORE_NAME } from "./LocalDatabase/StoreNames";
import getLocalDB from "./LocalDatabase/GetLocalDB";
import uuidv4 from "uuid/v4";

class UploadFilesService {
  
  constructor() {
    getLocalDB.then((result) => {
      this.localDB = result;
    });
  }

  saveFilesToDeleteOffline(files) {
    this.localDB.saveObject("app", "filesToDelete", files);
  }

  async getFilesToDelete() {
    try {
      return await this.localDB.getObject("app", "filesToDelete");
    } catch (err) {
      return [];
    }
  }

  async getOfflineKeys() {
    try {
      return await this.localDB.getObject("app", "offlineFileKeys");
    } catch (err) {
      return [];
    }
  }

  async clearCachedFilesExcept(fileGuid) {
    fileGuid = fileGuid.map((v) => {
      if (Array.isArray(v)) {
        v.forEach((element) => {
          element.toUpperCase();
        });
      } else {
        v.toUpperCase();
      }
    });
    let keys = (await this.getCachedKeys()).filter((v) => {
      fileGuid.indexOf(v) === -1;
    });
    for (let key of keys) {
      await this.uncacheFile(key);
    }
  }

  async getFullCacheRelatedKeys() {
    let keys = await this.getCachedKeys();
    if (keys.length > 0) {
      keys = keys.map((v) => "FILES_" + v);
      keys.push("__CACHEDFILEKEYS");
    }
    return keys;
  }

  saveOfflineKeys(offlineFileKeys) {
    this.localDB.saveObject("app", "offlineFileKeys", offlineFileKeys);
  }

  async getCachedKeys() {
    try {
      return await this.localDB.getObject("app", "cachedFileKeys");
    } catch (err) {
      return [];
    }
  }

  saveCachedKeys(cachedFileKeys) {
    this.localDB.saveObject("app", "cachedFileKeys", cachedFileKeys);
  }
  /**
   *
   * @param {File} file The file you want to Upload
   * @param {Function} progresscallback - optional callback for Progress event
   * @returns {Promise<R>} - returns the response in a Promise
   */
  async uploadOnline(file, onUploadProgress = (r) => {}) {
    let formData = new FormData();

    formData.append("file", file);
      let response = await http.post("api/Upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onUploadProgress,
      });
      let id = response.data[0].DownloadId;
      try {
        await this.cacheFile(id, file);
      } catch (error) {}
      return id;
  }

  /**
   *
   * @param {String} baseKey The first part of the local key
   * @param {File} file The file you want to Upload
   * @returns {Promise<R>} - returns the response in a Promise
   */
  async uploadOffline(baseKey, file) {
    let key = baseKey + "_" + uuidv4();
    await this.localDB.saveFile(FILES_STORE_NAME, key, file);
    let offlineFileKeys = await this.getOfflineKeys();
    offlineFileKeys.push(key);
    this.saveOfflineKeys(offlineFileKeys);
    return key;
  }

  /**
   *
   * @param {String} key The file key
   * @param {File} file The file you want to Upload
   * @returns {Promise<R>} - returns the response in a Promise
   */
  async cacheFile(key, file) {
    if(!await this.isCachedKey(key)) {
      await this.localDB.saveFile(FILES_STORE_NAME, key, file);
      let cachedFileKeys = await this.getCachedKeys();
      cachedFileKeys.push(key);
      this.saveCachedKeys(cachedFileKeys);
    }
  }

  /**
   *
   * @param {String} key The file key
   * @returns {Promise<R>} - returns the response in a Promise
   */
  async uncacheFile(key) {
    let cachedFileKeys = await this.getCachedKeys();
    let newCachedFileKeys = [];
    for (let item of cachedFileKeys) {
      if (item === key) {
        this.localDB.remove(FILES_STORE_NAME, key);
      } else {
        newCachedFileKeys.push(item);
      }
    }
    this.saveCachedKeys(newCachedFileKeys);
  }

  /**
   * @description Upload file that was saved in offline mode to the backend and return the new key (offline files are assigned bad GUIDs to mark them)
   * @param {String} key The file key
   * @returns {Promise<R>} - returns the response in a Promise
   */
  async uploadOfflineFile(key) {
    let file = await this.getLocalFile(key);
    let newKey = await this.uploadOnline(file);
    let offlineFileKeys = await this.getOfflineKeys();
    this.localDB.saveObject(
      "app",
      "offlineFileKeys",
      offlineFileKeys.filter((v) => v != key)
    );
    return { oldKey: key, newKey: newKey };
  }

  /**
   * @description Upload mulitple files that were saved in offline mode to the backend and return the new keys (offline files are assigned bad GUIDs to mark them)
   * @param {Array<String>} keys The list of keys to upload
   * @returns {Promise<Array<Object>>} - returns the response with array with new files keys
   */
  async uploadOfflineFiles(keys) {
    let newKeys = [];
    for (var key of keys) {
      if (await this.isOfflineKey(key)) {
        newKeys.push(await this.uploadOfflineFile(key));
      } else {
        newKeys.push({ oldKey: key, newKey: key });
      }
    }
    return newKeys;
  }
  /**
   * @description Get the file from local database if it was saved offline or download it from backend
   * NOTE: Caching does not make sense here since pictures aren't reused or downloaded multiple times
   * @param {String} key The file key (id)
   * @returns {blob} - returns the file as a blob
   */
  async getFileById(key) {
    if (!key) {
      console.debug("getFileById: !key");
      return;
    }

    if(await this.isOfflineKey(key) || await this.isCachedKey(key)) {
      return await this.getLocalFile(key);
    }

    let response = await http.get(`/api/Download/${key}`, {
      responseType: "blob",
    });

    return response.data;
  }

  /**
   *
   * @param {String} key The file key (id)
   * @returns {Promise<String>} - returns the response in a Promise
   */
  async getFileURLById(key) {
    let blob = await this.getFileById(key);
    if (blob && blob.size > 0) return URL.createObjectURL(blob);
  }

  /**
   * @description Check if the given file key belongs to a cached file
   * @param {String} key The file key
   * @returns {Promise<Boolean>} - returns if the key belongs to a cached file
   */
  async isCachedKey(key) {
    let isCached = false;
    isCached = (await this.getCachedKeys()).indexOf(key) > -1;
    return isCached;
  }

  /**
   * @description Check if the given file key (GUID or GUID with accessoires likes questioname) belongs to a file that was saved in offline mode (offline files are assigned bad GUIDs to mark them)
   * @param {String} key The file key
   * @returns {Promise<Boolean>} - returns if the key belongs to offline file
   */
  async isOfflineKey(key) {
    let isOffline = false;
    isOffline = (await this.getOfflineKeys()).indexOf(key) > -1;
    if (!isOffline) {
      // guid regex = [0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}
      // question number regex = q[0-9]{5}
      // values for offline keys: guid_guid (Ticket Uploads) or questionNumber_guid (Question Uploads), so we just look for .*_guid

      let isOfflineRegex = /.*_[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      isOffline = isOfflineRegex.test(key);
    }
    return isOffline;
  }

  /**
   * @description Get a file that was saved in offline mode from local browser database
   * @param {String} key The offline file key
   * @returns {Promise<blob>} - returns the file as a blob
   */
  async getLocalFile(key) {
    return await this.localDB.getFile(FILES_STORE_NAME, key);
  }

  /**
   *
   * @param  {Array<SurveyInstanceAnswerFile>}files JSON of files like saved for SurveyInstanceAnswer
   * @returns
   */
  delete(files) {
    files = files.filter((file) => { return !this.isOfflineKey(file); });
    return http.post("api/Download/Delete", JSON.stringify(files));
  }
}

export default new UploadFilesService();



// WEBPACK FOOTER //
// ./src/services/uploadService.js